// EditScreen.jsx - Livreur Zones
import React, { useState, useEffect, useMemo } from "react";
import { Container, Row, Col, Card, Form, Button, Alert, Table, Badge, Modal } from "react-bootstrap";
import { useHistory, useParams } from "react-router-dom";
import ClickBack from "../../components/buttons/ClickBack";
import {
  fetchLivreurZones,
  fetchAllZones,
  assignZoneToLivreur,
  updateZoneAssignment,
  deleteZoneAssignment,
} from "./dataService";
import IconTrash from "../../images/trash.png";

function EditScreen() {
  const { id } = useParams();
  const history = useHistory();
  const [livreur, setLivreur] = useState(null);
  const [currentZones, setCurrentZones] = useState([]);
  const [allZones, setAllZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newZoneData, setNewZoneData] = useState({
    zone_id: "",
    is_primary: false,
    priority: 1,
    notes: "",
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch current zones
      const zonesResp = await fetchLivreurZones(id);
      setLivreur(zonesResp.livreur);
      setCurrentZones(zonesResp.zones || []);

      // Fetch all available zones
      const allZonesResp = await fetchAllZones();
      setAllZones(allZonesResp || []);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateZone = async (assignmentId, field, value) => {
    try {
      // If updating is_primary to true, we need to unset others first
      if (field === "is_primary" && value) {
        // Update locally
        setCurrentZones(
          currentZones.map((z) => ({
            ...z,
            is_primary: z.id === assignmentId,
          }))
        );
      }

      await updateZoneAssignment(assignmentId, { [field]: value });

      // Refresh data
      await fetchData();
    } catch (err) {
      console.error("Error updating zone assignment:", err);
      alert("Erreur lors de la mise à jour");
    }
  };

  const handleDeleteZone = async (assignmentId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir retirer cette zone ?")) {
      return;
    }

    try {
      await deleteZoneAssignment(assignmentId);
      await fetchData(); // Refresh
      alert("Zone retirée avec succès");
    } catch (err) {
      console.error("Error deleting zone:", err);
      alert("Erreur lors de la suppression");
    }
  };

  const handleAddZone = async () => {
    if (!newZoneData.zone_id) {
      alert("Veuillez sélectionner une zone");
      return;
    }

    try {
      await assignZoneToLivreur(id, newZoneData);
      setShowAddModal(false);
      setNewZoneData({ zone_id: "", is_primary: false, priority: 1, notes: "" });
      await fetchData(); // Refresh
      alert("Zone assignée avec succès");
    } catch (err) {
      console.error("Error adding zone:", err);
      alert(err.response?.data?.message || "Erreur lors de l'assignation");
    }
  };

  // Get zones that are not yet assigned
  const availableZones = useMemo(() => {
    if (!Array.isArray(allZones)) return [];
    return allZones.filter(
      (zone) => !currentZones.some((cz) => cz.zone_id === zone.id)
    );
  }, [allZones, currentZones]);

  if (loading) {
    return (
      <Container fluid className="p-4 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="p-4">
        <Alert variant="danger">{error}</Alert>
        <Button onClick={() => history.goBack()}>Retour</Button>
      </Container>
    );
  }

  return (
    <Container fluid className="p-4">
      <Row className="mb-4">
        <Col>
          <ClickBack />
          <h2 className="mt-3">
            Modifier les zones de {livreur?.nom} {livreur?.prenom}
          </h2>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card>
            <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Zones assignées ({currentZones.length})</h5>
              <Button size="sm" variant="light" onClick={() => setShowAddModal(true)}>
                + Ajouter une zone
              </Button>
            </Card.Header>
            <Card.Body>
              {currentZones.length === 0 ? (
                <Alert variant="info">
                  Aucune zone assignée. Cliquez sur "Ajouter une zone" pour commencer.
                </Alert>
              ) : (
                <Table striped bordered>
                  <thead>
                    <tr>
                      <th>Zone</th>
                      <th>Type</th>
                      <th>Priorité</th>
                      <th>Statut</th>
                      <th>Notes</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentZones.map((assignment) => (
                      <tr key={assignment.id}>
                        <td>
                          <strong>{assignment.zone?.name || "Zone inconnue"}</strong>
                        </td>
                        <td>
                          <Form.Check
                            type="radio"
                            label="Principale"
                            name="primary_zone"
                            checked={assignment.is_primary}
                            onChange={() =>
                              handleUpdateZone(assignment.id, "is_primary", true)
                            }
                          />
                        </td>
                        <td>
                          <Form.Select
                            size="sm"
                            value={assignment.priority}
                            onChange={(e) =>
                              handleUpdateZone(assignment.id, "priority", parseInt(e.target.value))
                            }
                          >
                            {[1, 2, 3, 4, 5].map((p) => (
                              <option key={p} value={p}>
                                {p}
                              </option>
                            ))}
                          </Form.Select>
                        </td>
                        <td>
                          <Form.Check
                            type="switch"
                            checked={assignment.is_active}
                            onChange={(e) =>
                              handleUpdateZone(assignment.id, "is_active", e.target.checked)
                            }
                            label={assignment.is_active ? "Active" : "Inactive"}
                          />
                        </td>
                        <td>
                          <Form.Control
                            as="textarea"
                            rows={1}
                            size="sm"
                            value={assignment.notes || ""}
                            onChange={(e) =>
                              handleUpdateZone(assignment.id, "notes", e.target.value)
                            }
                            placeholder="Notes..."
                          />
                        </td>
                        <td>
                          <Button
                            variant="link"
                            className="p-0"
                            onClick={() => handleDeleteZone(assignment.id)}
                            title="Supprimer"
                          >
                            <img src={IconTrash} alt="Supprimer" width="20" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Add Zone Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Assigner une nouvelle zone</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {availableZones.length === 0 ? (
            <Alert variant="warning">
              Toutes les zones disponibles sont déjà assignées à ce livreur.
            </Alert>
          ) : (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Zone</Form.Label>
                <Form.Select
                  value={newZoneData.zone_id}
                  onChange={(e) =>
                    setNewZoneData({ ...newZoneData, zone_id: e.target.value })
                  }
                >
                  <option value="">Sélectionner une zone...</option>
                  {availableZones.map((zone) => (
                    <option key={zone.id} value={zone.id}>
                      {zone.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Zone principale"
                  checked={newZoneData.is_primary}
                  onChange={(e) =>
                    setNewZoneData({ ...newZoneData, is_primary: e.target.checked })
                  }
                />
                <Form.Text className="text-muted">
                  Le livreur ne peut avoir qu'une seule zone principale
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Priorité</Form.Label>
                <Form.Select
                  value={newZoneData.priority}
                  onChange={(e) =>
                    setNewZoneData({ ...newZoneData, priority: parseInt(e.target.value) })
                  }
                >
                  {[1, 2, 3, 4, 5].map((p) => (
                    <option key={p} value={p}>
                      {p} {p === 1 && "(Haute)"} {p === 5 && "(Basse)"}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  1 = Priorité haute, 5 = Priorité basse
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={newZoneData.notes}
                  onChange={(e) =>
                    setNewZoneData({ ...newZoneData, notes: e.target.value })
                  }
                  placeholder="Notes administratives..."
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>
            Annuler
          </Button>
          {availableZones.length > 0 && (
            <Button variant="primary" onClick={handleAddZone}>
              Assigner
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default EditScreen;
